<script setup>
import { ref, computed } from 'vue';
import ConversationCard from './ConversationCard.vue';

const props = defineProps({
  agent: {
    type: Object,
    default: null,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  isUnassigned: {
    type: Boolean,
    default: false,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  totalCount: {
    type: Number,
    default: null,
  },
  selectMode: {
    type: Boolean,
    default: false,
  },
  selectedIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['select-conversation', 'load-more', 'toggle-select', 'select-all-column']);

const columnTitle = computed(() => {
  if (props.isUnassigned) return 'Sem atribuição';
  return props.agent?.name || 'Agente';
});

const availabilityDot = computed(() => {
  if (props.isUnassigned) return '';
  const status = props.agent?.availability_status;
  if (status === 'online') return 'bg-green-500';
  if (status === 'busy') return 'bg-yellow-500';
  return 'bg-n-slate-7';
});

const conversationCount = computed(() => props.totalCount ?? props.conversations.length);

const allSelected = computed(() => {
  if (!props.conversations.length) return false;
  return props.conversations.every(c => props.selectedIds.includes(c.id));
});

const agentImgError = ref(false);
const onAgentImgError = () => {
  agentImgError.value = true;
};

// Infinite scroll handler
const onScroll = e => {
  const el = e.target;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
    if (props.hasMore && !props.isLoadingMore) {
      emit('load-more');
    }
  }
};
</script>

<template>
  <div
    class="flex-shrink-0 w-[300px] flex flex-col bg-n-alpha-1 rounded-xl border border-n-weak overflow-hidden"
  >
    <div
      class="flex items-center gap-2.5 px-3 py-3 border-b border-n-weak bg-n-background sticky top-0"
    >
      <template v-if="!isUnassigned">
        <div class="relative flex-shrink-0">
          <img
            v-if="agent?.thumbnail && !agentImgError"
            :src="agent.thumbnail"
            :alt="agent.name"
            class="size-8 rounded-full object-cover ring-1 ring-n-weak"
            @error="onAgentImgError"
          />
          <div
            v-else
            class="size-8 rounded-full bg-n-brand/10 text-n-brand flex items-center justify-center text-sm font-semibold"
          >
            {{ (agent?.name || '?')[0].toUpperCase() }}
          </div>
          <span
            class="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-n-background"
            :class="availabilityDot"
          />
        </div>
        <div class="min-w-0 flex-grow">
          <p class="text-xs font-semibold text-n-slate-12 truncate">
            {{ agent?.name }}
          </p>
          <p class="text-[10px] text-n-slate-9 capitalize">
            {{ agent?.availability_status || 'offline' }}
          </p>
        </div>
      </template>
      <template v-else>
        <div
          class="size-8 rounded-full bg-n-slate-3 dark:bg-n-slate-9 flex items-center justify-center flex-shrink-0"
        >
          <span
            class="i-lucide-user-x size-4 text-n-slate-9 dark:text-n-slate-5"
          />
        </div>
        <div class="min-w-0 flex-grow">
          <p class="text-xs font-semibold text-n-slate-12">Sem atribuição</p>
          <p class="text-[10px] text-n-slate-9">Conversas não atribuídas</p>
        </div>
      </template>
      <span
        class="flex-shrink-0 text-[10px] font-bold bg-n-brand/10 text-n-brand px-2 py-0.5 rounded-full"
      >
        {{ conversationCount }}
      </span>
      <input
        v-if="selectMode && conversations.length"
        type="checkbox"
        :checked="allSelected"
        class="size-4 flex-shrink-0 accent-blue-500 cursor-pointer"
        @click.stop="emit('select-all-column', conversations)"
      />
    </div>
    <div
      class="flex-grow overflow-y-auto p-2 space-y-2 no-scrollbar"
      @scroll="onScroll"
    >
      <ConversationCard
        v-for="conversation in conversations"
        :key="conversation.id"
        :conversation="conversation"
        :select-mode="selectMode"
        :selected="selectedIds.includes(conversation.id)"
        @click="emit('select-conversation', conversation)"
        @toggle-select="emit('toggle-select', $event)"
      />
      <div v-if="isLoadingMore" class="flex items-center justify-center py-3">
        <span class="i-lucide-loader-2 size-4 text-n-slate-9 animate-spin" />
      </div>
      <div
        v-if="!conversations.length && !isLoadingMore"
        class="flex flex-col items-center justify-center py-8 text-center"
      >
        <span class="i-lucide-coffee size-8 text-n-slate-7 mb-2" />
        <p class="text-xs text-n-slate-9">Nenhuma conversa</p>
      </div>
    </div>
  </div>
</template>
